import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, Navigate, Link, useLocation } from 'react-router-dom';
import {
  FiArchive,
  FiBarChart2,
  FiBox,
  FiClipboard,
  FiHome,
  FiMenu,
  FiPackage,
  FiSettings,
  FiShoppingBag,
  FiTruck,
  FiUsers,
  FiX,
  FiLayers,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import './AdminLayout.css';
import './AdminUI.css';

export function AdminHomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === 'admin' ? 'products/new' : 'orders'} replace />;
}

const NAV = [
  { to: '/admin/products/new', label: 'Добавить товар', icon: FiPackage, adminOnly: true },
  { to: '/admin/categories', label: 'Категории', icon: FiLayers, adminOnly: true },
  { to: '/admin/inventory', label: 'Склад', icon: FiBox },
  { to: '/admin/sales', label: 'Продажа', icon: FiShoppingBag },
  { to: '/admin/orders', label: 'Заказы', icon: FiClipboard },
  { to: '/admin/reports', label: 'Отчёты', icon: FiBarChart2 },
  { to: '/admin/users', label: 'Пользователи', icon: FiUsers },
  { to: '/admin/archive', label: 'Документы', icon: FiArchive },
  { to: '/admin/seller-profiles', label: 'Реквизиты', icon: FiSettings, adminOnly: true },
  { to: '/admin/settings', label: 'Доставка', icon: FiTruck, adminOnly: true },
];

const TITLES = {
  '/admin/products/new': 'Добавить товар',
  '/admin/categories': 'Категории',
  '/admin/inventory': 'Склад / Остатки',
  '/admin/sales': 'Ручная продажа',
  '/admin/orders': 'Заказы',
  '/admin/reports': 'Отчёты',
  '/admin/users': 'Пользователи',
  '/admin/archive': 'Документы',
  '/admin/seller-profiles': 'Реквизиты',
  '/admin/settings': 'Доставка',
};

function pageTitle(pathname) {
  if (pathname.includes('/admin/orders/') && pathname !== '/admin/orders') return 'Заказ';
  if (pathname.includes('/admin/products/') && pathname.endsWith('/edit')) return 'Редактирование товара';
  const exact = TITLES[pathname];
  if (exact) return exact;
  const hit = Object.keys(TITLES).find((k) => pathname.startsWith(k));
  return hit ? TITLES[hit] : 'Админ-панель';
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = useMemo(
    () => NAV.filter((item) => (item.adminOnly ? isAdmin : true)),
    [isAdmin]
  );

  const title = pageTitle(location.pathname);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const nav = (
    <nav className="admin-nav" aria-label="Разделы админки">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) => 'admin-link' + (isActive ? ' is-active' : '')}
        >
          <Icon aria-hidden />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="admin-shell admin-ui">
      <aside className={`admin-sidebar ${menuOpen ? 'is-open' : ''}`}>
        <div className="admin-sidebar__top">
          <Link to="/admin" className="admin-brand">
            <img src="/logo2.png" alt="" />
            <div>
              <strong>LavSan</strong>
              <span>Админ-панель</span>
            </div>
          </Link>
          <button
            type="button"
            className="admin-sidebar__close"
            aria-label="Закрыть меню"
            onClick={() => setMenuOpen(false)}
          >
            <FiX />
          </button>
        </div>

        {nav}

        <div className="admin-sidebar__foot">
          <div className="admin-user">
            <span className="admin-user__role">{isAdmin ? 'Админ' : 'Менеджер'}</span>
            <span className="admin-user__name">{user?.name || user?.phone || 'Пользователь'}</span>
          </div>
          <Link to="/" className="admin-sidebar__store">
            <FiHome /> На сайт
          </Link>
          <button type="button" className="admin-sidebar__logout" onClick={logout}>
            Выйти
          </button>
        </div>
      </aside>

      <div
        className={`admin-backdrop ${menuOpen ? 'is-open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />

      <div className="admin-main">
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-topbar__menu"
            aria-label="Открыть меню"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <FiMenu />
          </button>
          <div className="admin-topbar__title">
            <h1>{title}</h1>
          </div>
          <Link to="/" className="admin-topbar__home">
            <FiHome />
            <span>На сайт</span>
          </Link>
        </header>

        <div className="admin-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
